<template>
	<div class="tabs">
		<div ref="tabsHeaderRef" class="tabs__header">
			<button
				v-for="(tab, i) in tabs"
				:ref="(el) => setTabButtonRef(el, i)"
				:key="i"
				type="button"
				class="tabs__header-btn"
				:class="{ active: activeTab === i, soon: tab.soon }"
				:data-label="$rt(tab.title)"
				@click="handleTabClick(i)">
				<span class="tabs__header-btn-title">
					{{ $rt(tab.title) }}
				</span>
				<span v-if="tab.soon" class="tabs__header-btn-soon">
					{{ $rt(tab.soon) }}
				</span>
			</button>
			<span class="tabs__header-indicator" :style="indicatorStyle" />
		</div>
		<div class="tabs__content">
			<!-- <Transition name="fade" mode="out-in"> -->
			<div :key="activeTab">
				<slot :name="`tab-content-${activeTab + 1}`"></slot>
			</div>
			<!-- </Transition> -->
		</div>
	</div>
</template>
<script setup lang="ts">
	import type { ComponentPublicInstance } from 'vue'

	type TabButtonElement = HTMLButtonElement | null

	const activeTab = ref<number>(0)
	const tabsHeaderRef = ref<HTMLDivElement | null>(null)
	const tabButtonRefs = ref<TabButtonElement[]>([])
	const indicatorStyle = ref({
		width: '0px',
		transform: 'translateX(0px)',
	})

	defineProps<{
		tabs: {
			title: string
			soon?: string
		}[]
	}>()

	const setTabButtonRef = (el: Element | ComponentPublicInstance | null, index: number) => {
		if (el instanceof HTMLButtonElement) {
			tabButtonRefs.value[index] = el
			return
		}

		tabButtonRefs.value[index] = null
	}

	const updateIndicator = (index: number) => {
		const tabsHeaderEl = tabsHeaderRef.value
		const tabButtonEl = tabButtonRefs.value[index]
		if (!tabsHeaderEl || !tabButtonEl) {
			return
		}

		const { left: headerLeft } = tabsHeaderEl.getBoundingClientRect()
		const { left: buttonLeft, width } = tabButtonEl.getBoundingClientRect()
		const offsetLeft = buttonLeft - headerLeft + tabsHeaderEl.scrollLeft

		indicatorStyle.value = {
			width: `${width}px`,
			transform: `translateX(${offsetLeft}px)`,
		}
	}

	const handleTabClick = (index: number) => {
		activeTab.value = index
		const tabButtonEl = tabButtonRefs.value[index]
		if (tabButtonEl) {
			tabButtonEl.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			})
		}
		updateIndicator(index)
	}

	onMounted(() => {
		nextTick(() => {
			updateIndicator(activeTab.value)
		})
	})
</script>
<style lang="scss" scoped>
	.tabs {
		&__header {
			position: relative;
			display: flex;
			overflow: overlay clip;
			scrollbar-width: none;
			border-bottom: 1px solid var(--border);
			-ms-overflow-style: none;
			&::-webkit-scrollbar {
				display: none;
			}
			&-btn {
				display: flex;
				gap: 10px;
				padding: 12px 20px;
				white-space: nowrap;
				cursor: pointer;
				background: transparent;
				border: 0;
				position: relative;
				// border: 1px solid #000;
				&::after {
					content: attr(data-label);
					position: absolute;
					bottom: 0px;
					left: 0;
					right: 0;
					background: transparent;
					width: 100%;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
					font-weight: 600;
					opacity: 0;
					transition: 0.4s opacity;
				}
				&-title{
					letter-spacing: 0.3px;
					transition: 0.4s opacity;
				}
				&.active {
					.tabs__header-btn-title {
						opacity: 0;
					}
					&::after {
						opacity: 1;
					}
				}
				&.soon {
					pointer-events: none;
					cursor: default;
					.tabs__header-btn-title {
						opacity: 0.5;
					}
				}
				&-soon {
					position: relative;
					top: -8px;
					padding: 3px 7px;
					font-size: 12px;
					font-weight: bold;
					background-color: var(--primary);
					border-radius: 40px;
				}
			}
			&-indicator {
				position: absolute;
				bottom: 0;
				left: 0;
				height: 2px;
				pointer-events: none;
				background: var(--primary);
				transition:
					transform 0.3s ease,
					width 0.3s ease;
			}
		}
		&__content {
			margin-top: 32px;
		}
	}
</style>
